// re-push trigger
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import BackButton from '../components/BackButton';

const BASE_URL = 'https://education-backend.liara.run/api';

const AdminLessonManager = () => {
  // Accept multiple possible storage keys used by your login flow
  const storedRole = localStorage.getItem('role');            // optional
  const storedAdminToken = localStorage.getItem('adminToken');//
  const storedTeacherRole = localStorage.getItem('teacherRole');
  const storedTeacherToken = localStorage.getItem('teacherToken');

  // Determine effective role and token without changing login
  const effectiveRole = (storedRole && storedRole === 'Admin') ? 'Admin'
                      : (storedTeacherRole && storedTeacherRole === 'Admin') ? 'Admin'
                      : null;

  const effectiveToken = storedAdminToken ?? storedTeacherToken ?? null;

  const [authError, setAuthError] = useState(false);

  const [grades, setGrades] = useState([]);
  const [newGrade, setNewGrade] = useState({ level: '', stream: '' });
  const [selectedGradeId, setSelectedGradeId] = useState(null);

  const [lessons, setLessons] = useState([]);
  const [newLessonTitle, setNewLessonTitle] = useState('');
  const [newLessonTeacherIds, setNewLessonTeacherIds] = useState('');

  const [editingGradeId, setEditingGradeId] = useState(null);
  const [editingGrade, setEditingGrade] = useState({ level: '', stream: '' });

  const [editingLessonId, setEditingLessonId] = useState(null);
  const [editingLessonTitle, setEditingLessonTitle] = useState('');
  const [editingLessonTeacherIds, setEditingLessonTeacherIds] = useState('');

  const axiosConfig = {
    headers: { Authorization: `Bearer ${effectiveToken}` }
  };

  useEffect(() => {
    // If the login saved admin under teacherRole or admin role exists, accept it
    if (effectiveRole !== 'Admin' || !effectiveToken) {
      setAuthError(true);
      return;
    }
    fetchGrades();
  }, []); // eslint-disable-line

  // ---------- Grades ----------
  const fetchGrades = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/grade`, axiosConfig);
      setGrades(res.data);
    } catch (err) {
      console.error('❌ خطا در گرفتن پایه‌ها:', err);
      alert('خطا در گرفتن پایه‌ها از سرور.');
    }
  };

  const handleAddGrade = async () => {
    if (!newGrade.level?.trim()) {
      alert('فیلد پایه لازم است.');
      return;
    }
    try {
      const res = await axios.post(
        `${BASE_URL}/grade`,
        { level: newGrade.level.trim(), stream: newGrade.stream?.trim() },
        axiosConfig
      );
      setNewGrade({ level: '', stream: '' });
      await fetchGrades();
      const created = res.data;
      const newId = created?.Id ?? created?.id;
      if (newId) fetchLessons(newId);
    } catch (err) {
      console.error('❌ خطا در ثبت پایه:', err);
      alert('خطا در ثبت پایه.');
    }
  };

  const handleUpdateGrade = async () => {
    if (!editingGradeId) return;
    if (!editingGrade.level?.trim()) {
      alert('فیلد پایه لازم است.');
      return;
    }
    try {
      await axios.put(
        `${BASE_URL}/grade/${editingGradeId}`,
        { level: editingGrade.level.trim(), stream: editingGrade.stream?.trim() },
        axiosConfig
      );
      setEditingGradeId(null);
      setEditingGrade({ level: '', stream: '' });
      fetchGrades();
    } catch (err) {
      console.error('❌ خطا در ویرایش پایه:', err);
      alert('خطا در ویرایش پایه.');
    }
  };

  const handleDeleteGrade = async (id) => {
    if (!window.confirm('آیا از حذف این پایه مطمئن هستید؟ حذف باعث حذف دروس مرتبط می‌شود.')) return;
    try {
      await axios.delete(`${BASE_URL}/grade/${id}`, axiosConfig);
      if (selectedGradeId === id) {
        setSelectedGradeId(null);
        setLessons([]);
      }
      fetchGrades();
    } catch (err) {
      console.error('❌ خطا در حذف پایه:', err);
      alert('خطا در حذف پایه.');
    }
  };

  // ---------- Lessons ----------
  const fetchLessons = async (gradeId) => {
    if (!gradeId) return;
    try {
      const res = await axios.get(`${BASE_URL}/lesson?gradeId=${gradeId}`, axiosConfig);
      setLessons(res.data);
      setSelectedGradeId(gradeId);
    } catch (err) {
      console.error('❌ خطا در گرفتن درس‌ها:', err);
      alert('خطا در گرفتن درس‌ها.');
    }
  };

  const handleAddLesson = async () => {
    if (!selectedGradeId) { alert('ابتدا یک پایه را انتخاب کنید.'); return; }
    if (!newLessonTitle?.trim()) { alert('عنوان درس لازم است.'); return; }

    let teacherIds = undefined;
    if (newLessonTeacherIds && newLessonTeacherIds.trim()) {
      teacherIds = newLessonTeacherIds
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .map(Number)
        .filter(n => !Number.isNaN(n) && n > 0);
      if (teacherIds.length === 0) teacherIds = undefined;
    }

    try {
      const body = { title: newLessonTitle.trim(), gradeId: selectedGradeId };
      if (teacherIds) body.teacherIds = teacherIds;
      await axios.post(`${BASE_URL}/lesson`, body, axiosConfig);
      setNewLessonTitle('');
      setNewLessonTeacherIds('');
      fetchLessons(selectedGradeId);
    } catch (err) {
      console.error('❌ خطا در ثبت درس:', err);
      alert('خطا در ثبت درس.');
    }
  };

  const handleUpdateLesson = async () => {
    if (!editingLessonId) return;
    if (!editingLessonTitle?.trim()) { alert('عنوان درس لازم است.'); return; }

    let teacherIds = undefined;
    if (editingLessonTeacherIds && editingLessonTeacherIds.trim()) {
      teacherIds = editingLessonTeacherIds
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .map(Number)
        .filter(n => !Number.isNaN(n) && n > 0);
      if (teacherIds.length === 0) teacherIds = undefined;
    }

    try {
      const body = { title: editingLessonTitle.trim(), gradeId: selectedGradeId };
      if (teacherIds) body.teacherIds = teacherIds;
      await axios.put(`${BASE_URL}/lesson/${editingLessonId}`, body, axiosConfig);
      setEditingLessonId(null);
      setEditingLessonTitle('');
      setEditingLessonTeacherIds('');
      fetchLessons(selectedGradeId);
    } catch (err) {
      console.error('❌ خطا در ویرایش درس:', err);
      alert('خطا در ویرایش درس.');
    }
  };

  const handleDeleteLesson = async (id) => {
    if (!window.confirm('آیا از حذف این درس مطمئن هستید؟')) return;
    try {
      await axios.delete(`${BASE_URL}/lesson/${id}`, axiosConfig);
      fetchLessons(selectedGradeId);
    } catch (err) {
      console.error('❌ خطا در حذف درس:', err);
      alert('خطا در حذف درس.');
    }
  };

  // ---------- Render ----------
  if (authError) {
    return (
      <div dir="rtl" style={{ padding: '2rem', color: 'red' }}>
        ❌ دسترسی نامعتبر. تنها مدیران می‌توانند از این بخش استفاده کنند.
      </div>
    );
  }

  return (
    <div dir="rtl" style={{ padding: '2rem' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <BackButton toFallback="/dashboard" />
      </header>

      <h2>📚 مدیریت پایه‌ها و درس‌ها (Admin)</h2>

      <section style={{ marginBottom: '2rem' }}>
        <h4>➕ ثبت پایه جدید</h4>
        <input
          type="text"
          placeholder="پایه (مثلاً دهم)"
          value={newGrade.level}
          onChange={(e) => setNewGrade({ ...newGrade, level: e.target.value })}
        />
        <input
          type="text"
          placeholder="رشته (مثلاً کامپیوتر)"
          value={newGrade.stream}
          onChange={(e) => setNewGrade({ ...newGrade, stream: e.target.value })}
        />
        <button onClick={handleAddGrade}>ثبت پایه</button>
      </section>

      <section>
        <h4>📋 لیست پایه‌ها</h4>
        {grades.length === 0 && <div>هیچ پایه‌ای ثبت نشده.</div>}
        {grades.map((grade) => (
          <div key={grade.id} style={{ marginBottom: '1rem', border: '1px solid #ddd', padding: '1rem' }}>
            {editingGradeId === grade.id ? (
              <>
                <input
                  value={editingGrade.level}
                  onChange={(e) => setEditingGrade({ ...editingGrade, level: e.target.value })}
                  placeholder="پایه"
                />
                <input
                  value={editingGrade.stream}
                  onChange={(e) => setEditingGrade({ ...editingGrade, stream: e.target.value })}
                  placeholder="رشته"
                />
                <button onClick={handleUpdateGrade}>ذخیره</button>
                <button onClick={() => setEditingGradeId(null)}>لغو</button>
              </>
            ) : (
              <>
                <strong>{grade.level} {grade.stream ? `- ${grade.stream}` : ''}</strong>
                <div style={{ marginTop: 8 }}>
                  <button onClick={() => {
                    setEditingGradeId(grade.id);
                    setEditingGrade({ level: grade.level, stream: grade.stream || '' });
                  }}>✏️</button>
                  <button onClick={() => handleDeleteGrade(grade.id)}>🗑️</button>
                  <button onClick={() => fetchLessons(grade.id)} style={{ marginLeft: 12 }}>مشاهده و افزودن درس</button>
                </div>
              </>
            )}

            {selectedGradeId === grade.id && (
              <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: '#fafafa' }}>
                <h5>➕ افزودن درس به این پایه</h5>
                <input
                  type="text"
                  placeholder="عنوان درس"
                  value={newLessonTitle}
                  onChange={(e) => setNewLessonTitle(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="اختیاری: teacherIds (مثلاً 2,3)"
                  value={newLessonTeacherIds}
                  onChange={(e) => setNewLessonTeacherIds(e.target.value)}
                />
                <button onClick={handleAddLesson}>ثبت درس</button>

                <h5 style={{ marginTop: '1rem' }}>📚 درس‌های این پایه</h5>
                {lessons.length === 0 && <div>برای این پایه درسی ثبت نشده.</div>}
                <ul>
                  {lessons.map((lesson) => (
                    <li key={lesson.id} style={{ marginBottom: 8 }}>
                      {editingLessonId === lesson.id ? (
                        <>
                          <input
                            value={editingLessonTitle}
                            onChange={(e) => setEditingLessonTitle(e.target.value)}
                            placeholder="عنوان درس"
                          />
                          <input
                            value={editingLessonTeacherIds}
                            onChange={(e) => setEditingLessonTeacherIds(e.target.value)}
                            placeholder="اختیاری: teacherIds (مثلاً 2,3)"
                          />
                          <button onClick={handleUpdateLesson}>ذخیره</button>
                          <button onClick={() => setEditingLessonId(null)}>لغو</button>
                        </>
                      ) : (
                        <>
                          <span style={{ marginRight: 8 }}>{lesson.title}</span>
                          <button onClick={() => {
                            setEditingLessonId(lesson.id);
                            setEditingLessonTitle(lesson.title || '');
                            setEditingLessonTeacherIds('');
                          }}>✏️</button>
                          <button onClick={() => handleDeleteLesson(lesson.id)}>🗑️</button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </section>
    </div>
  );
};

export default AdminLessonManager;